// Which provider a page offers, and how each exchanges its code
// (02-DESIGN/01-identity/03-signing-in.md § the browser, § sign-up and the
// platform provider; 01-the-console.md § signing in): a tenant's page runs the
// one provider the workspace signs in through (decision 0055); the
// neutral page runs the platform provider. Where the provider admits a public
// browser client the page exchanges the code itself; where it does not, the
// page posts the code to the auth service's same-origin endpoint, which
// trades it with the secret the service holds.

import { PLATFORM_NAME, providerName } from "../platform/names.ts";
import type { PlatformProfile, Provider, TenantProfile } from "../platform/profile.ts";
import type { Exchange } from "./tokens.ts";

/** Choice is one provider a page offers. */
export interface Choice {
  /**
   * What the button says: *Continue with Impire* for the platform's, *Continue
   * with* the display name for a workspace's own (01-the-console.md § signing in).
   */
  label: string;
  lane: "oidc" | "github";
  /** The tenant the sign-in names, or empty on the identity plane. */
  slug: string;
  provider: Provider;
  clientId: string;
  exchange: Exchange;
  /** Whether this is the platform provider (the identity plane's, and every tenant's by inheritance). */
  platform: boolean;
}

/** The path of the GitHub lane's exchange at the console's origin, as the design names it. */
export const GITHUB_EXCHANGE_PATH = "/auth/github/token";

/** tenantExchangePath is the token-exchange endpoint for a tenant's own provider. */
export function tenantExchangePath(slug: string): string {
  return `/auth/${encodeURIComponent(slug)}/token`;
}

/** browserClientId is the client id registered for this surface: the per-surface one where the provider forces it. */
export function browserClientId(p: Provider): string {
  return p.client_ids?.browser ?? p.client_id ?? "";
}

/** ChoiceError says why a provider cannot be offered. */
export class ChoiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChoiceError";
  }
}

/** PLATFORM_LABEL is the platform's button, whatever product the install runs beneath it (decision 0049). */
const PLATFORM_LABEL = `Continue with ${PLATFORM_NAME}`;

function platformChoice(origin: string, platform: PlatformProfile, slug: string): Choice {
  const p = platform.platform_provider;
  if (p.lane === "github") {
    return {
      label: PLATFORM_LABEL,
      lane: "github",
      slug,
      provider: p,
      clientId: browserClientId(p),
      exchange: {
        kind: "server-side",
        url: new URL(platform.token_exchange_url ?? GITHUB_EXCHANGE_PATH, origin).toString(),
      },
      platform: true,
    };
  }
  if (p.lane !== "oidc") {
    throw new ChoiceError(
      `the sign-in this install offers everyone ("${p.lane}") is not one a browser can use`,
    );
  }
  const clientId = browserClientId(p);
  if (p.exchange === "server-side") {
    const url = platform.token_exchange_url ?? (slug === "" ? undefined : tenantExchangePath(slug));
    if (url === undefined) {
      throw new ChoiceError(
        "the sign-in this install offers everyone exchanges its code through Impire, but the profile names no exchange URL",
      );
    }
    return {
      label: PLATFORM_LABEL,
      lane: "oidc",
      slug,
      provider: p,
      clientId,
      exchange: { kind: "server-side", url: new URL(url, origin).toString() },
      platform: true,
    };
  }
  if (p.token_endpoint === undefined || p.token_endpoint === "") {
    throw new ChoiceError("the sign-in this install offers everyone names no token endpoint");
  }
  return {
    label: PLATFORM_LABEL,
    lane: "oidc",
    slug,
    provider: p,
    clientId,
    exchange: { kind: "public", url: p.token_endpoint, clientId },
    platform: true,
  };
}

function ownChoice(origin: string, tenant: TenantProfile, p: Provider | undefined): Choice | undefined {
  if (p === undefined) {
    return undefined;
  }
  if (p.lane !== "oidc") {
    return undefined;
  }
  if (p.surfaces !== undefined && !p.surfaces.includes("browser")) {
    return undefined;
  }
  const clientId = browserClientId(p);
  const exchange: Exchange =
    p.exchange === "server-side"
      ? { kind: "server-side", url: new URL(tenantExchangePath(tenant.slug), origin).toString() }
      : { kind: "public", url: p.token_endpoint ?? "", clientId };
  return {
    label: `Continue with ${providerName(p)}`,
    lane: "oidc",
    slug: tenant.slug,
    provider: p,
    clientId,
    exchange,
    platform: false,
  };
}

/**
 * choices lists the one provider a page offers (decision 0055): on a
 * tenant's page the provider the workspace signs in through — its own or its
 * managed one where the profile names one, the platform provider otherwise;
 * on the neutral page the platform provider. Never two.
 */
export function choices(origin: string, platform: PlatformProfile, tenant: TenantProfile | null): Choice[] {
  if (tenant !== null && tenant.provider !== undefined) {
    const own = ownChoice(origin, tenant, tenant.provider);
    // A provider the browser cannot use offers nothing; the platform's would be refused.
    return own === undefined ? [] : [own];
  }
  return [platformChoice(origin, platform, tenant?.slug ?? "")];
}

/**
 * proofChoice is the provider a switch is proven through
 * (03-signing-in.md § switching a workspace's provider): the workspace's own
 * as the provider read holds it, a managed provider, or the platform's.
 */
export function proofChoice(
  origin: string,
  platform: PlatformProfile,
  slug: string,
  provider: Provider | "platform",
): Choice | undefined {
  if (provider === "platform") {
    return platformChoice(origin, platform, slug);
  }
  return ownChoice(origin, { slug } as TenantProfile, { ...provider, lane: "oidc" });
}
